"use client"

import { useEffect, useRef, useState } from "react"

import { ArrowIcon, LinkButton, Text } from "@/components/ui"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"
import { itemAt } from "@/lib/helpers/array"

interface ScaleWallProps {
  count: number
  sentence: string
  images: ({ url: string; alt?: string } & ImageCrop)[]
  cta?: string
  ctaHref?: string
}

const CELLS = 72
const COUNT_UP_MS = 1500

const useCountUp = (target: number) => {
  const [value, setValue] = useState(target)
  const anchor = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = anchor.current
    if (!node) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    setValue(0)
    let frame = 0
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        const start = performance.now()
        const step = (now: number) => {
          const progress = Math.min(1, (now - start) / COUNT_UP_MS)
          setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))))
          if (progress < 1) frame = requestAnimationFrame(step)
        }
        frame = requestAnimationFrame(step)
      },
      { threshold: 0.4 }
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [target])

  return { value, anchor }
}

export const ScaleWall = ({ count, sentence, images, cta, ctaHref }: ScaleWallProps) => {
  const { value, anchor } = useCountUp(count)

  return (
    <div ref={anchor} className="@container">
      <div className="rounded-20x bg-fg-strong relative isolate flex min-h-105 flex-col justify-end overflow-hidden px-600 py-800 @2xl:px-1200 @2xl:py-1600">
        <div aria-hidden className="grid-cols-muro absolute inset-0 z-0 grid auto-rows-fr gap-100 overflow-hidden opacity-40 brightness-90 grayscale @2xl:opacity-35">
          {images.length > 0 &&
            Array.from({ length: CELLS }, (_, index) => (
              <span key={index} className="rounded-4x bg-nelo-black-soft relative grid place-items-center overflow-hidden">
                <TileImage
                  src={itemAt(images, index % images.length).url}
                  crop={itemAt(images, index % images.length)}
                  alt={itemAt(images, index % images.length).alt ?? ""}
                  fill
                  sizes="72px"
                  className="scale-70 object-contain"
                />
              </span>
            ))}
        </div>
        <span aria-hidden className="from-fg-strong via-fg-strong/85 to-fg-strong/65 absolute inset-0 z-10 bg-linear-to-t" />
        <div className="relative z-20 flex flex-col items-start">
          <h2 className="font-display text-fg-inverted font-extrabold">
            <b className="text-muro block font-extrabold tabular-nums">{value.toLocaleString("es-MX")}</b>
            <Text as="span" variant="lede" family="text" tone="invertedMuted" className="max-w-measure-sm mt-500 block font-normal">
              {sentence}
            </Text>
          </h2>
          {cta && (
            <LinkButton href={ctaHref} variant="primary" size="lg" className="lift mt-700 font-extrabold" iconRight={<ArrowIcon size={18} />}>
              {cta}
            </LinkButton>
          )}
        </div>
      </div>
    </div>
  )
}
